<script lang="ts">
  import { onMount } from 'svelte'
  import Modal from '@/components/shared/modals/Modal.svelte'
  import Review from '@/components/draft-review/Review.svelte'

  interface Props {
    draftsUrl: string
    initialCount: number
  }

  let { draftsUrl, initialCount }: Props = $props()

  interface DraftItem {
    id: string
    fire_hashid: string
    trigger_name: string
    group_names: string[]
    group_count: number
    body_preview: string
    age_iso: string
    status: string
    review_url: string
    api_url: string
    send_url: string
    discard_url: string
  }

  function formatGroupList(names: string[]): string {
    if (!names || names.length === 0) return '—'
    if (names.length <= 2) return names.join(', ')
    return `${names[0]}, ${names[1]}, +${names.length - 2} more`
  }

  let loading = $state(true)
  let fetchError = $state<string | null>(null)
  let drafts = $state<DraftItem[]>([])

  // Modal state. Two flags so fade-in/out CSS transitions can play:
  //   - activeDraft: keeps the Modal mounted (drives {#if})
  //   - showModal:   drives Modal's `show` prop (toggles the .active class)
  // On open: mount first (showModal=false), then flip to true next frame so CSS sees the transition.
  // On close: flip showModal=false to start fade-out, delay unmount until the 250ms transition completes.
  let activeDraft = $state<DraftItem | null>(null)
  let showModal = $state(false)
  const FADE_MS = 250  // matches `transition: opacity 0.25s` on .modal-window

  function csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
  }

  function openReview(draft: DraftItem) {
    activeDraft = draft
    showModal = true   // fade-in is handled via CSS @keyframes, not by toggling .active
  }

  function closeReview() {
    if (!showModal) return  // ignore spurious calls (e.g. before opening completes)
    showModal = false        // removes .active → CSS opacity transition fades out
    setTimeout(() => { activeDraft = null }, FADE_MS)
  }

  async function refetchDrafts() {
    try {
      const resp = await fetch(draftsUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!resp.ok) return
      const data = await resp.json()
      drafts = data.drafts ?? []
      updateOutsideUI(drafts.length)
    } catch {
      /* swallow — list stays as-is */
    }
  }

  // After a send/discard, sync the outside-this-component UI (site-wide
  // "you have N drafts" banner + the Drafts subnav badge) so the count
  // doesn't lag the actual state.
  function updateOutsideUI(count: number) {
    const banner = document.querySelector<HTMLElement>('.js-drafts-banner')
    const bannerText = document.querySelector<HTMLElement>('.js-drafts-banner-text')
    if (banner && bannerText) {
      if (count === 0) {
        banner.style.display = 'none'
      } else {
        const noun = count === 1 ? 'draft' : 'drafts'
        bannerText.innerHTML = `You have ${count} ${noun} awaiting your review &rarr;`
        banner.style.display = ''
      }
    }

    const badge = document.querySelector<HTMLElement>('.js-drafts-tab-badge')
    if (badge) {
      if (count === 0) {
        badge.style.display = 'none'
        badge.textContent = ''
      } else {
        badge.style.display = ''
        badge.textContent = String(count)
      }
    }
  }

  function handleComplete() {
    closeReview()
    refetchDrafts()
  }

  function timeAgo(isoString: string): string {
    const now = Date.now()
    const then = new Date(isoString).getTime()
    const diffMs = now - then
    const diffSec = Math.floor(diffMs / 1000)
    const diffMin = Math.floor(diffSec / 60)
    const diffHr = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHr / 24)

    if (diffSec < 60) return `${diffSec}s ago`
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    return `${diffDay}d ago`
  }

  function statusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending_review: 'Pending Review',
      failed: 'Failed',
      expired: 'Expired',
    }
    return labels[status] ?? status
  }

  function statusClass(status: string): string {
    const classes: Record<string, string> = {
      pending_review: 'badge-warning',
      failed: 'badge-danger',
      expired: 'badge-default',
    }
    return classes[status] ?? 'badge-default'
  }

  onMount(async () => {
    try {
      const resp = await fetch(draftsUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      drafts = data.drafts ?? []
    } catch (err) {
      fetchError = err instanceof Error ? err.message : 'Failed to load drafts'
    } finally {
      loading = false
    }
  })
</script>

<div class="drafts-list">
  {#if loading}
    <div class="loading-info-box">Loading drafts...</div>
  {:else if fetchError}
    <div class="alert alert-danger">{fetchError}</div>
  {:else if drafts.length === 0}
    <div class="blank-state">
      <p>No drafts awaiting review.</p>
    </div>
  {:else}
    <table class="table table-striped drafts-table">
      <thead>
        <tr>
          <th>Trigger</th>
          <th>Group</th>
          <th>Preview</th>
          <th>Age</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each drafts as draft (draft.id)}
          <tr>
            <td class="drafts-trigger-name">{draft.trigger_name ?? '—'}</td>
            <td class="drafts-group-name" title={draft.group_names?.join(', ') ?? ''}>{formatGroupList(draft.group_names)}</td>
            <td class="drafts-preview">{draft.body_preview}</td>
            <td class="drafts-age">{timeAgo(draft.age_iso)}</td>
            <td>
              <span class="badge {statusClass(draft.status)}">
                {statusLabel(draft.status)}
              </span>
            </td>
            <td>
              <button type="button" class="btn btn-sm btn-primary" onclick={() => openReview(draft)}>
                Review
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>

{#if activeDraft}
  {@const draft = activeDraft}
  <Modal id="draft-review-modal" show={showModal} class="drafts-review-modal" onClose={closeReview}>
    {#snippet title()}
      Review draft
    {/snippet}
    {#snippet content()}
      <Review
        fireHashid={draft.fire_hashid}
        apiUrl={draft.api_url}
        sendUrl={draft.send_url}
        discardUrl={draft.discard_url}
        csrfToken={csrfToken()}
        onComplete={handleComplete}
      />
    {/snippet}
  </Modal>
{/if}

<style>
  /* Portaled out of this component, so :global() is required to reach it. */
  :global(.drafts-review-modal .modal-inner) {
    width: 75vw;
    max-width: 75vw;
  }
  /* Center the × glyph in the close button — the global modal-close
     CSS uses magic-number nudges that drift off at this modal's width. */
  :global(.drafts-review-modal .modal-close.btn) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  :global(.drafts-review-modal .modal-close.btn::before) {
    margin-left: 0 !important;
    left: 0 !important;
    position: static !important;
    line-height: 1 !important;
  }

  /* Fade-in animation runs once on mount (CSS transition alone won't
     trigger because .active is present from the first paint). Fade-out
     is handled by the existing .modal-window opacity transition when
     .active is removed before unmount. */
  :global(.drafts-review-modal .modal-window.active) {
    animation: drafts-modal-fade-in 250ms ease;
  }
  @keyframes drafts-modal-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
</style>
